import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AppManagement = ({ currentUser, onAppUpdated }) => {
  const [apps, setApps] = useState([]);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingIcons, setLoadingIcons] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' или 'grid'

  const [formData, setFormData] = useState({
    name: '',
    icon: '📱',
    url: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchApps();
    fetchAvailableIcons();
  }, []);

  useEffect(() => { const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(t); }, [success, error]);

  const fetchApps = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/apps/', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setApps(response.data.apps);
      if (onAppUpdated) {
        onAppUpdated();
      }
    } catch (error) {
      setError('Не удалось загрузить список приложений. Пожалуйста, попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableIcons = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoadingIcons(true);
    try {
      const response = await axios.get('/api/apps/icons/available', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setAvailableIcons(response.data.icons);
    } catch (error) {
      console.error('Ошибка загрузки иконок:', error);
    } finally {
      setLoadingIcons(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;

    setError('');
    setSuccess('');

    try {
      if (editingApp) {
        await axios.put(`/api/apps/${editingApp.id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setSuccess('Приложение успешно обновлено!');
      } else {
        await axios.post('/api/apps/', formData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setSuccess('Приложение успешно создано!');
      }

      setShowModal(false);
      setEditingApp(null);
      resetForm();
      fetchApps();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Не удалось сохранить приложение. Пожалуйста, попробуйте снова.');
      }
    }
  };

  const handleEdit = (app) => {
    setEditingApp(app);
    setFormData({
      name: app.name,
      icon: app.icon,
      url: app.url,
      description: app.description || '',
      is_active: app.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (appId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/apps/${appId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSuccess('Приложение успешно удалено!');
      setDeleteConfirm(null);
      fetchApps();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Не удалось удалить приложение. Пожалуйста, попробуйте снова.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      icon: '📱',
      url: '',
      description: '',
      is_active: true
    });
  };

  const openCreateModal = () => {
    setEditingApp(null);
    resetForm();
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Н/Д';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const launchApp = (url) => {
    if (url) {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'http://' + url;
      }
      window.open(url, '_blank');
    }
  };

  const selectIcon = (iconValue) => {
    setFormData({ ...formData, icon: iconValue });
  };

  if (loading) {
    return (
      <div className="app-management">
        <div className="content-header">
          <h1>Управление приложениями</h1>
          <p>Управление приложениями системы</p>
        </div>
        <div className="loading">Загрузка приложений...</div>
      </div>
    );
  }

  return (
    <div className="app-management">
      <div className="content-header">
        <div>
          <h1>Управление приложениями</h1>
          <p>Управление приложениями системы</p>
        </div>
        <div className="header-actions">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button 
                className={`btn-small ${viewMode === 'table' ? 'btn-secondary' : 'btn-icon'}`}
                onClick={() => setViewMode('table')}
              >
                📋 Таблица
              </button>
              <button 
                className={`btn-small ${viewMode === 'grid' ? 'btn-secondary' : 'btn-icon'}`}
                onClick={() => setViewMode('grid')}
              >
                🏠 Сетка
              </button>
            </div>
            <button className="btn-success btn-small" onClick={openCreateModal}>
              + Добавить приложение
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {apps.length === 0 ? (
        <div className="no-data">
          Приложения не найдены. Создайте первое приложение, чтобы начать.
        </div>
      ) : viewMode === 'table' ? (
        <table className="app-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Иконка</th>
              <th>URL</th>
              <th>Описание</th>
              <th>Статус</th>
              <th>Создано</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {apps.map((app) => (
              <tr key={app.id}>
                <td>{app.id}</td>
                <td>
                  <strong>{app.name}</strong>
                </td>
                <td>
                  <div style={{ fontSize: '20px' }}>{app.icon}</div>
                </td>
                <td>
                  <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {app.url}
                  </div>
                </td>
                <td>
                  <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {app.description || 'Нет описания'}
                  </div>
                </td>
                <td>
                  <span className={`badge ${app.is_active ? 'badge-active' : 'badge-inactive'}`}>
                    {app.is_active ? 'Активно' : 'Неактивно'}
                  </span>
                </td>
                <td>{formatDate(app.created_at)}</td>
                <td>
                  <div className="app-actions">
                    <button 
                      className="btn-secondary btn-small btn-icon"
                      onClick={() => launchApp(app.url)}
                      title="Запустить приложение"
                    >
                      🚀 Запустить
                    </button>
                    <button 
                      className="btn-secondary btn-small"
                      onClick={() => handleEdit(app)}
                    >
                      Редактировать
                    </button>
                    <button 
                      className="btn-danger btn-small"
                      onClick={() => setDeleteConfirm(app)}
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="app-grid">
          {apps.map((app) => (
            <div key={app.id} className="app-card">
              <div className="app-card-header">
                <div className="app-icon">{app.icon}</div>
                <div className="app-info">
                  <div className="app-name">
                    {app.name}
                    <span className={`badge ${app.is_active ? 'badge-active' : 'badge-inactive'}`}>
                      {app.is_active ? 'Активно' : 'Неактивно'}
                    </span>
                  </div>
                  <div className="app-url">{app.url}</div>
                </div>
              </div>
              
              {app.description && (
                <div className="app-description">{app.description}</div>
              )}
              
              <div className="app-footer">
                <div className="app-created">
                  Создано: {formatDate(app.created_at)}
                </div>
                <div className="app-actions">
                  <button 
                    className="btn-success btn-small btn-icon"
                    onClick={() => launchApp(app.url)}
                    title="Запустить приложение"
                  >
                    🚀 Запустить
                  </button>
                  <button 
                    className="btn-secondary btn-small"
                    onClick={() => handleEdit(app)}
                  >
                    ✏️
                  </button>
                  <button 
                    className="btn-danger btn-small"
                    onClick={() => setDeleteConfirm(app)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingApp ? 'Редактировать приложение' : 'Создать новое приложение'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Название приложения *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Введите название приложения"
                  title="Название, отображаемое в списке приложений на главной"
                />
              </div>
              
              <div className="form-group">
                <label>Иконка приложения</label>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <div className="icon-preview">
                    {formData.icon}
                  </div>
                  <span>Текущая иконка: {formData.icon}</span>
                </div>
                
                {loadingIcons ? (
                  <div>Загрузка иконок...</div>
                ) : (
                  <div className="icon-selector">
                    {availableIcons.map((icon) => (
                      <div
                        key={icon.value}
                        className={`icon-option ${formData.icon === icon.value ? 'selected' : ''}`}
                        onClick={() => selectIcon(icon.value)}
                        title={icon.label}
                      >
                        {icon.value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>URL приложения *</label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                  placeholder="https://example.com или example.com"
                  title="Ссылка на приложение. Можно указать с https:// или без — откроется в новой вкладке"
                />
              </div>
              
              <div className="form-group">
                <label>Описание (необязательно)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Введите описание приложения"
                  rows="3"
                  title="Краткое описание приложения для подсказки пользователям"
                />
              </div>
              
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <label htmlFor="is_active">Активное приложение</label>
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="btn-success">
                  {editingApp ? 'Обновить приложение' : 'Создать приложение'}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingApp(null);
                    resetForm();
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirmation-dialog">
            <h3>Подтверждение удаления</h3>
            <p>
              Вы уверены, что хотите удалить приложение <strong>"{deleteConfirm.name}"</strong>?
              Это действие нельзя отменить.
            </p>
            <div className="confirmation-actions">
              <button 
                className="btn-danger btn-small"
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                Удалить
              </button>
              <button 
                className="btn-secondary btn-small"
                onClick={() => setDeleteConfirm(null)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppManagement;
